export interface ApiResult<T = any> {
  ok: boolean;
  status: number;
  data: T | null;
  message: string;
}

/**
 * Small fetch wrapper: attaches the bearer token, sends JSON bodies, and always
 * resolves (never throws) with a normalised { ok, data, message } result.
 */
export async function authFetch<T = any>(
  token: string | null | undefined,
  url: string,
  options: { method?: string; body?: unknown; form?: FormData } = {}
): Promise<ApiResult<T>> {
  try {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    let body: BodyInit | undefined;
    if (options.form) {
      body = options.form;
    } else if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(options.body);
    }

    const response = await fetch(url, { method: options.method ?? 'GET', headers, body });

    let data: any = null;
    const text = await response.text();
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = null;
      }
    }

    const message =
      data?.message ?? (response.ok ? '' : response.status === 403 ? 'You do not have access to this.' : 'Request failed.');

    return { ok: response.ok && data?.success !== false, status: response.status, data, message };
  } catch (err) {
    console.error(err);
    return { ok: false, status: 0, data: null, message: 'Could not reach the server.' };
  }
}

/**
 * Opens a protected file (one that needs the bearer token) in a new tab.
 * The tab is opened synchronously so popup blockers allow it, then pointed at the downloaded blob.
 * Returns an error message, or null on success.
 */
export async function openAuthedFile(token: string | null | undefined, url: string): Promise<string | null> {
  const tab = window.open('', '_blank');
  try {
    const response = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!response.ok) {
      tab?.close();
      return response.status === 404 || response.status === 403 ? 'You do not have access to this file.' : 'Could not open the file.';
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    if (tab) tab.location.href = objectUrl;
    else window.location.href = objectUrl;
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    return null;
  } catch (err) {
    console.error(err);
    tab?.close();
    return 'Could not open the file.';
  }
}

// Indexed by the server's StudentDocumentType value: 0 ID, 1 Matric, 2 Affidavit, 3 CV.
export const DOCUMENT_TYPE_LABELS = [
  'Certified ID Copy',
  'Matric (With Mathematics) Certified Certificate',
  'Affidavit (Not on any SETA Programme)',
  'CV',
];
// Order shown to users: Matric, ID, CV, Affidavit.
export const DOCUMENT_DISPLAY_ORDER = [1, 0, 3, 2];
export const DOCUMENT_STATUS_LABELS = ['Pending review', 'Verified', 'Rejected'];
export const DOCUMENT_STATUS_BADGE = ['bg-amber-100 text-amber-700', 'bg-green-100 text-green-700', 'bg-red-100 text-red-700'];

export const formatDate =(value: string | null | undefined) =>
  value ? new Date(value).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

export const formatDateTime = (value: string | null | undefined) =>
  value
    ? new Date(value).toLocaleString('en-ZA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

export const MODULE_TYPE_LABELS = ['Knowledge', 'Practical', 'Workplace'];

export const MODULE_TYPE_BADGE = [
  'bg-blue-100 text-blue-700',
  'bg-amber-100 text-amber-700',
  'bg-purple-100 text-purple-700',
];

export const MODERATION_LABELS = ['Agreed', 'Disagreed', 'Referred back'];
export const MODERATION_BADGE = [
  'bg-green-100 text-green-700',
  'bg-red-100 text-red-700',
  'bg-amber-100 text-amber-700',
];
